import { lazyScene } from './lazy-scene';
import { SceneSkeletonPreviewScene } from './scene-skeleton';
import { CANCELLATION_SURFACE_MAX_HEIGHT_CLASS, PAUSE_DOWNLOAD_WARNING_SCREEN_TEXT } from '../Modals/CancelSubscription/constants';
import type { SceneRegistry } from './types';

// Google Identity Services silently renders nothing above a 400px button
// width and its final iframe can't be styled, so the auth dialog narrows to
// meet the button instead: a 400px content column plus the screens' 40px
// x-padding and 1px surface border each side. The native Google button
// renders at exactly GSI's 400px cap and fills the column, matching the
// adjacent fields (DEV-6289). On the sub-600px fullscreen surface the same
// invariant is kept by the screens themselves, which cap their column at
// 448px (400px + their 24px padding each side).
const authSurfaceClassName = {
  modal: 'max-w-[482px]'
};
const cancellationSurfaceClassName = {
  modal: CANCELLATION_SURFACE_MAX_HEIGHT_CLASS,
  fullscreen: 'top-[56px] h-[calc(100dvh-56px)]'
};
const LoginScene = lazyScene(() => import('./scenes/auth/login'));
const ForgotPasswordScene = lazyScene(() => import('./scenes/auth/forgot-password'));
const ResetPasswordScene = lazyScene(() => import('./scenes/auth/reset-password'));
const SignupOptionsScene = lazyScene(() => import('./scenes/auth/signup-options'));
const SignupEmailScene = lazyScene(() => import('./scenes/auth/signup-email'));
const AuthGateScene = lazyScene(() => import('./scenes/onboarding/auth-gate'));
const DownloadPremiumGateScene = lazyScene(() => import('./scenes/download/premium-gate'));
const DownloadSignupScene = lazyScene(() => import('./scenes/download/signup'));
const DownloadPremiumSignupScene = lazyScene(() => import('./scenes/download/premium-signup'));
const DownloadPlanChoiceScene = lazyScene(() => import('./scenes/download/plan-choice'));
const DownloadLoginScene = lazyScene(() => import('./scenes/download/login'));
const DownloadUsageScene = lazyScene(() => import('./scenes/download/usage'));
const DownloadPlanPickerScene = lazyScene(() => import('./scenes/download/plan-picker'));
const DownloadReadyScene = lazyScene(() => import('./scenes/download/ready'));
const PlanStepScene = lazyScene(() => import('./scenes/onboarding/plan-step'));
const QuestionnaireScene = lazyScene(() => import('./scenes/onboarding/questionnaire'));
const PartnerApplicationScene = lazyScene(() => import('./scenes/partner/application'));
const PartnerSuccessScene = lazyScene(() => import('./scenes/partner/success'));
const PartnerNotEligibleScene = lazyScene(() => import('./scenes/partner/not-eligible'));
const VerifyEmailScene = lazyScene(() => import('./scenes/onboarding/verify-email'));
const ManageSubscriptionScene = lazyScene(() => import('./scenes/cancellation/manage-subscription'));
const ChangePlanScene = lazyScene(() => import('./scenes/cancellation/change-plan'));
const ConfirmPlanChangeScene = lazyScene(() => import('./scenes/cancellation/confirm-plan-change'));
const SwitchToYearlyScene = lazyScene(() => import('./scenes/cancellation/switch-to-yearly'));
const SwitchToMonthlyScene = lazyScene(() => import('./scenes/cancellation/switch-to-monthly'));
const PauseOrCancelScene = lazyScene(() => import('./scenes/cancellation/pause-or-cancel'));
const PausingMeansScene = lazyScene(() => import('./scenes/cancellation/pausing-means'));
const PauseDownloadWarningScene = lazyScene(() => import('./scenes/cancellation/pause-download-warning'));
const PauseDurationScene = lazyScene(() => import('./scenes/cancellation/pause-duration'));
const PauseSafelistChannelsScene = lazyScene(() => import('./scenes/cancellation/pause-safelist-channels'));
const PauseConfirmationScene = lazyScene(() => import('./scenes/cancellation/pause-confirmation'));
const ResumeScene = lazyScene(() => import('./scenes/cancellation/resume'));
const ResumeConfirmationScene = lazyScene(() => import('./scenes/cancellation/resume-confirmation'));
const WhyLeaveScene = lazyScene(() => import('./scenes/cancellation/why-leave'));
const OfferScene = lazyScene(() => import('./scenes/cancellation/offer'));
const OfferClaimedScene = lazyScene(() => import('./scenes/cancellation/offer-claimed'));
const RemoveSafelistScene = lazyScene(() => import('./scenes/cancellation/remove-safelist'));
const ConfirmCancelScene = lazyScene(() => import('./scenes/cancellation/confirm-cancel'));
const CancelledScene = lazyScene(() => import('./scenes/cancellation/cancelled'));

/**
 * Single source of truth for every scene in the app. Adding a flow means
 * adding entries here (and the matching key in {@link ./types}); the
 * `satisfies` clause enforces that both stay in lock-step.
 *
 * Cross-flow handoffs are just `goTo` calls between keys in different
 * namespaces — the stage swaps content under one shared overlay, so there's
 * no flicker and no per-pair wiring.
 */
export const scenes = {
  'download.premium-gate': {
    content: DownloadPremiumGateScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    a11y: {
      title: 'Get this premium track'
    }
  },
  'download.premium-signup': {
    content: DownloadPremiumSignupScene,
    // Reuses the auth signup screen, so it shares the narrowed auth width.
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Create your account'
    }
  },
  'download.plan-choice': {
    content: DownloadPlanChoiceScene,
    // Same layout as `onboarding.plan-step`, so the same width.
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: {
      modal: 'max-w-[864px]'
    },
    a11y: {
      title: 'Pick your plan'
    }
  },
  'download.signup': {
    content: DownloadSignupScene,
    // Reuses the auth signup screen, so it inherits the same narrowed
    // width that keeps the GSI Google button rendering (see note above).
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Create your free account to download'
    }
  },
  'download.login': {
    content: DownloadLoginScene,
    // Reuses the auth login screen, so it shares the narrowed auth width.
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Log in to download'
    }
  },
  'download.usage': {
    content: DownloadUsageScene,
    // Full-page on mobile rather than a drawer (DEV-6515). Uses the stage's
    // default 90dvh modal cap (raised from 80dvh per DEV-6621 so the revealed
    // plan panel fits without scrolling on regular desktop screens);
    // DialogScreens owns the inner scroll when it still overflows.
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    a11y: {
      title: 'Where will you use this asset?'
    }
  },
  'download.plan-picker': {
    content: DownloadPlanPickerScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    a11y: {
      title: 'Find the right plan'
    }
  },
  'download.ready': {
    content: DownloadReadyScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    a11y: {
      title: 'Ready to download'
    }
  },
  'auth.login': {
    content: LoginScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Log in'
    }
  },
  'auth.forgot-password': {
    content: ForgotPasswordScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Reset your password'
    }
  },
  'auth.reset-password': {
    content: ResetPasswordScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Set a new password'
    }
  },
  'auth.signup-options': {
    content: SignupOptionsScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Create your account'
    }
  },
  'auth.signup-email': {
    content: SignupEmailScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Create your account with email'
    }
  },
  'onboarding.auth-gate': {
    content: AuthGateScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    a11y: {
      title: 'Sign in to continue'
    }
  },
  'onboarding.plan-step': {
    content: PlanStepScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    // Wider than the auth screens it follows; the stage animates the width
    // change alongside the height (see the modal surface transition).
    surfaceClassName: {
      modal: 'max-w-[864px]'
    },
    a11y: {
      title: 'Pick your plan'
    }
  },
  'onboarding.questionnaire': {
    content: QuestionnaireScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    // On platforms with classic scrollbars (Windows), the scrollbar
    // appearing when the dialog is height-clamped steals 17px of width,
    // which rewraps the option chips and shifts everything below them —
    // painted as juddering/overlapping rows on some machines (DEV-6508).
    // Reserving the gutter keeps the chip layout identical whether or not
    // the scrollbar is present; overlay-scrollbar platforms are unaffected.
    screensClassName: '[scrollbar-gutter:stable]',
    a11y: {
      title: 'About you',
      description: 'Help us personalize your Uppbeat experience.'
    }
  },
  'onboarding.verify-email': {
    content: VerifyEmailScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    // Part of the signup flow, so it shares the narrowed auth dialog width
    // per DEV-6289's design rather than the stage's default 616px.
    surfaceClassName: authSurfaceClassName,
    a11y: {
      title: 'Verify your email',
      description: 'Check your inbox to verify your email address.'
    }
  },
  'partner.application': {
    content: PartnerApplicationScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    a11y: {
      title: 'Become a Partner',
      description: 'Apply to join the Uppbeat Partner Program.'
    }
  },
  'partner.success': {
    content: PartnerSuccessScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    a11y: {
      title: "You're in",
      description: 'Welcome to the Partner Program.'
    }
  },
  'partner.not-eligible': {
    content: PartnerNotEligibleScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    a11y: {
      title: "You're already a Partner"
    }
  },
  'cancellation.manage-subscription': {
    content: ManageSubscriptionScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Manage subscription'
    }
  },
  'cancellation.change-plan': {
    content: ChangePlanScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Change plan'
    }
  },
  'cancellation.confirm-plan-change': {
    content: ConfirmPlanChangeScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Confirm plan change'
    }
  },
  'cancellation.switch-to-yearly': {
    content: SwitchToYearlyScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Switch to yearly'
    }
  },
  'cancellation.switch-to-monthly': {
    content: SwitchToMonthlyScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Switch to monthly'
    }
  },
  'cancellation.pause-or-cancel': {
    content: PauseOrCancelScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Pause or cancel subscription'
    }
  },
  'cancellation.pause-download-warning': {
    content: PauseDownloadWarningScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: PAUSE_DOWNLOAD_WARNING_SCREEN_TEXT.TITLE
    }
  },
  'cancellation.pausing-means': {
    content: PausingMeansScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Why do you want to pause?'
    }
  },
  'cancellation.pause-duration': {
    content: PauseDurationScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Pause subscription'
    }
  },
  'cancellation.pause-safelist-channels': {
    content: PauseSafelistChannelsScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Pause safelisted channels'
    }
  },
  'cancellation.pause-confirmation': {
    content: PauseConfirmationScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Pause subscription confirmation'
    }
  },
  'cancellation.resume': {
    content: ResumeScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Confirm resume subscription'
    }
  },
  'cancellation.resume-confirmation': {
    content: ResumeConfirmationScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Subscription resumed'
    }
  },
  'cancellation.why-leave': {
    content: WhyLeaveScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Why do you want to cancel?'
    }
  },
  'cancellation.offer': {
    content: OfferScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Stay and get a discount'
    }
  },
  'cancellation.offer-claimed': {
    content: OfferClaimedScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Special offer applied'
    }
  },
  'cancellation.remove-safelist': {
    content: RemoveSafelistScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Your safelisted channels'
    }
  },
  'cancellation.confirm-cancel': {
    content: ConfirmCancelScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Confirm cancellation'
    }
  },
  'cancellation.cancelled': {
    content: CancelledScene,
    surface: {
      default: 'modal',
      mobile: 'fullscreen'
    },
    surfaceClassName: cancellationSurfaceClassName,
    hideMobileClose: true,
    a11y: {
      title: 'Subscription cancelled'
    }
  },
  // Dev-only preview of the shared scene skeleton. Triggered from the
  // Admin Bar's Modal Preview tool — gives designers / engineers a way
  // to inspect the skeleton outside of a real loading state.
  'dev.scene-skeleton': {
    content: SceneSkeletonPreviewScene,
    surface: {
      default: 'modal',
      mobile: 'drawer'
    },
    a11y: {
      title: 'Scene skeleton (preview)'
    }
  }
} satisfies SceneRegistry;